import React from 'react'
import styled from 'styled-components'

import ConfigurationAndComponentsCard from 'pages/HehmDevice/HehmDashboard/ConfigurationAndComponentsCard'
import LastUpdatedText from 'components/LastUpdatedText'
import RuntimeHistoryCard from 'pages/HehmDevice/HehmDashboard/RuntimeHistoryCard'
import SystemConditionCard from 'pages/HehmDevice/HehmDashboard/SystemConditionCard'
import AlertCard from 'pages/HehmDevice/HehmDashboard/AlertCard'
import NotesCard from 'pages/HehmDevice/HehmDashboard/NotesCard'
import TabContentTitle from 'pages/HehmDevice/TabContentTitle'
import { screenSize } from 'styles/shared'

import useScreenSize from 'hooks/useScreenSize'
import { useSessionContext } from 'hooks/SessionContext'

import HehmDevice, { HehmDeviceEquipment, HehmDeviceHardware } from 'types/HehmDevice'
import { DeviceBase } from 'types/Device'

type Props = {
  hehmDevice: HehmDevice | null
  hehmDeviceEquipment: HehmDeviceEquipment | null
  hehmDeviceHardware: HehmDeviceHardware | null
  onNavigate: (tab: string) => void
  device: DeviceBase
}

const HehmDashboard = ({ hehmDevice, hehmDeviceEquipment, hehmDeviceHardware, onNavigate, device }: Props) => {
  const screenSize = useScreenSize()
  const { session } = useSessionContext()

  return (
    <>
      <TabContentTitle>HVAC Performance Monitoring Kit Dashboard</TabContentTitle>
      <LastUpdatedText timestamp={Date.now()} />

      {screenSize === 'large' && (
        <S.Grid>
          <S.SysConditionArea>
            <SystemConditionCard hehmDevice={hehmDevice} onNavigate={onNavigate} />
          </S.SysConditionArea>
          <S.AlertArea>
            <AlertCard device={device} onNavigate={onNavigate} />
          </S.AlertArea>
          <S.RuntimeArea>
            <RuntimeHistoryCard onNavigate={onNavigate} />
          </S.RuntimeArea>
          {!session.isFsr && (
            <S.NotesArea>
              <NotesCard data={hehmDevice ?? undefined} onNavigate={onNavigate} device={device} />
            </S.NotesArea>
          )}
          <S.ConfigArea>
            <ConfigurationAndComponentsCard {...{ hehmDeviceEquipment, hehmDeviceHardware, onNavigate }} />
          </S.ConfigArea>
        </S.Grid>
      )}

      {screenSize !== 'large' && (
        <S.MediumAndSmallScreenContainer>
          <S.SysConditionArea>
            <SystemConditionCard hehmDevice={hehmDevice} onNavigate={onNavigate} />
          </S.SysConditionArea>
          <S.AlertArea>
            <AlertCard device={device} onNavigate={onNavigate} />
          </S.AlertArea>
          <S.RuntimeArea>
            <RuntimeHistoryCard onNavigate={onNavigate} />
          </S.RuntimeArea>
          <S.ConfigArea>
            <ConfigurationAndComponentsCard {...{ hehmDeviceEquipment, hehmDeviceHardware, onNavigate }} />
          </S.ConfigArea>
          {!session.isFsr && (
            <S.MediumNotesArea>
              <NotesCard data={hehmDevice ?? undefined} onNavigate={onNavigate} device={device} />
            </S.MediumNotesArea>
          )}
        </S.MediumAndSmallScreenContainer>
      )}
    </>
  )
}

export default HehmDashboard

const S = {
  Grid: styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr auto;
    grid-template-areas:
      "sys alerts config"
      "sys runtime config"
      "notes . .";
    column-gap: 10px;
    row-gap: 10px;
    margin-bottom: 10px;
  `,
  SysConditionArea: styled.div`
    grid-area: sys;
    display: flex;
    flex-direction: column;
    > * { flex: 1; }
  `,
  AlertArea: styled.div`
    grid-area: alerts;
    display: flex;
    flex-direction: column;
    > * { flex: 1; }
  `,
  RuntimeArea: styled.div`
    grid-area: runtime;
    display: flex;
    flex-direction: column;
    > * { flex: 1; }
  `,
  NotesArea: styled.div`
    grid-area: notes;
  `,
  ConfigArea: styled.div`
    grid-area: config;
    display: flex;
    flex-direction: column;
    > * { flex: 1; }
  `,
  MediumAndSmallScreenContainer: styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr auto;
    grid-template-areas:
      "sys alerts"
      "sys runtime"
      "notes config";
    column-gap: 10px;
    row-gap: 10px;
    margin-bottom: 10px;

    @media ${screenSize.small} {
      grid-template-columns: 1fr;
      grid-template-rows: auto;
      grid-template-areas:
        "sys"
        "alerts"
        "runtime"
        "config"
        "notes";
      gap: 10px;
    }
  `,
  MediumNotesArea: styled.div`
    grid-area: notes;
    align-self: start;
  `
}
